/* LanguageSwitcher — compact glassy dropdown showing the current
 * language's flag + native name. Opens a menu of the 12 supported
 * locales. Sits nicely inside the Login top-bar and can also be used
 * inside marketing headers.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Globe } from 'lucide-react';
import { useI18n, LANGUAGES } from '../i18n/I18nContext';

export default function LanguageSwitcher({ compact = false, align = 'right' }) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const active = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        data-testid="language-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 rounded-full glass border border-white/10 hover:bg-white/10 transition-colors ${
          compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1.5 text-[11px]'
        }`}
        aria-label={t('languageSwitcher.label', 'Language')}
      >
        <Globe className="w-3 h-3 text-primary-light" />
        <span className="text-base leading-none">{active.flag}</span>
        {!compact && <span className="tracking-widest uppercase">{active.code}</span>}
        <ChevronDown className="w-3 h-3 opacity-70" />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-switcher-menu"
          className={`absolute z-50 mt-2 w-48 rounded-2xl glass border border-white/10 py-1.5 backdrop-blur-xl shadow-2xl shadow-black/40 max-h-[70vh] overflow-y-auto ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              role="option"
              aria-selected={l.code === lang}
              data-testid={`language-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-white/10 transition-colors ${
                l.code === lang ? 'text-white' : 'text-white/80'
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span className="flex-1 text-left">{l.native}</span>
              {l.code === lang && <Check className="w-3.5 h-3.5 text-emerald-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
